import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Rightchat from "../components/Rightchat";
import Appfooter from "../components/Appfooter";
import Popupchat from "../components/Popupchat";

import { getClubs, getClubCategories } from "../redux/slices/clubSlice";
import { followClient } from "../redux/slices/clientFollowSlice";

const UPLOAD_URL = (
  process.env.REACT_APP_API_URL || "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/, "")
  .replace(/\/$/, "");

const getUploadUrl = (file?: string | null) => {
  if (!file) return "";

  const value = String(file).trim();

  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }

  if (value.startsWith("/uploads/")) {
    return `${UPLOAD_URL}${value}`;
  }

  return `${UPLOAD_URL}/uploads/${value}`;
};

const ClubsPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const { clubs = [], categories = [], loading } = useSelector(
    (state: any) => state.club
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    dispatch(getClubs());
    dispatch(getClubCategories());
  }, [dispatch]);

  const filteredClubs = useMemo(() => {
    let list = Array.isArray(clubs) ? clubs : [];

    if (selectedCategory) {
      list = list.filter(
        (club: any) => String(club.level_id) === String(selectedCategory)
      );
    }

    const search = searchTerm.toLowerCase().trim();

    if (search) {
      list = list.filter(
        (club: any) =>
          club.client_name?.toLowerCase().includes(search) ||
          club.level_name?.toLowerCase().includes(search)
      );
    }

    return list;
  }, [clubs, searchTerm, selectedCategory]);

  const handleFollow = async (club: any) => {
    if (!user?.user_id) {
      toast.error("Please login first");
      return;
    }

    const res = await dispatch(
      followClient({
        user_id: user.user_id,
        client_id: club.client_id,
      })
    );

    if (followClient.fulfilled.match(res) && res.payload?.status === true) {
      toast.success(res.payload?.message || "Club followed successfully");
      dispatch(getClubs());
    } else {
      toast.error(res.payload?.message || "Failed to follow club");
    }
  };

  const handleViewPosts = (club: any) => {
    const slug = String(club?.unique_slug || "").trim();

    if (slug) {
      // Example: /club/prasad
      navigate(`/club/${encodeURIComponent(slug)}`);
      return;
    }

    // Fallback when unique_slug is unavailable.
    navigate(`/club/${club.client_id}`);
  };

  return (
    <Fragment>
      <Header />
      <Leftnav />
      <Rightchat />

      <div className="main-content right-chat-active clubs-main-page">
        <div className="middle-sidebar-bottom clubs-middle-bottom">
          <div className="middle-sidebar-left clubs-middle-left">
            <div className="clubs-page-wrapper">
              <div className="clubs-header-card">
                <div className="clubs-header-top">
                  <div>
                    <span className="clubs-page-badge">Community Clubs</span>

                    <h2 className="clubs-title">Explore Clubs</h2>

                    <p className="clubs-subtitle">
                      Discover communities, follow clubs and view latest posts.
                    </p>
                  </div>

                  <div className="clubs-count-box">
                    <strong>{filteredClubs.length}</strong>
                    <span>Clubs</span>
                  </div>
                </div>

                <div className="clubs-filter-row">
                  <div className="clubs-search-box">
                    <i className="feather-search" />

                    <input
                      type="text"
                      placeholder="Search clubs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="clubs-select-wrap">
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="">All Categories</option>

                      {categories.map((cat: any) => (
                        <option key={cat.level_id} value={cat.level_id}>
                          {cat.level_name}
                        </option>
                      ))}
                    </select>

                    <i className="feather-chevron-down" />
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="clubs-loader-card">
                  <div className="spinner-border text-primary" />
                  <p>Loading clubs...</p>
                </div>
              ) : filteredClubs.length > 0 ? (
                <div className="clubs-grid">
                  {filteredClubs.map((club: any) => {
                    const logoUrl = getUploadUrl(club.logo_image);

                    return (
                      <div className="club-card" key={club.client_id}>
                        <div className="club-top">
                          <div className="club-avatar">
                            {logoUrl ? (
                              <img
                                src={logoUrl}
                                alt={club.client_name || "Club"}
                                onError={(e: any) => {
                                  e.currentTarget.style.display = "none";
                                  e.currentTarget.parentElement
                                    ?.querySelector(".club-avatar-fallback")
                                    ?.classList.add("show");
                                }}
                              />
                            ) : null}

                            <div
                              className={`club-avatar-fallback ${
                                !logoUrl ? "show" : ""
                              }`}
                            >
                              <i className="feather-users" />
                            </div>
                          </div>

                          <div className="club-main-info">
                            <h4>{club.client_name || "-"}</h4>
                            <span>{club.level_name || "Club"}</span>
                          </div>
                        </div>

                        <div className="club-stats-row">
                          <div className="club-stat-box">
                            <h5>{club.total_posts || 0}</h5>
                            <span>Posts</span>
                          </div>

                          <div className="club-stat-box">
                            <h5>{club.total_followers || 0}</h5>
                            <span>Followers</span>
                          </div>
                        </div>

                        <div className="club-action-row">
                          {Number(club.is_following) === 1 ? (
                            <button
                              type="button"
                              className="club-btn club-btn-following"
                              disabled
                            >
                              <i className="feather-check-circle" />
                              Following
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="club-btn club-btn-follow"
                              onClick={() => handleFollow(club)}
                            >
                              <i className="feather-plus" />
                              Follow
                            </button>
                          )}

                          <button
                            type="button"
                            className="club-btn club-btn-view"
                            onClick={() => handleViewPosts(club)}
                          >
                            View Posts
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="clubs-empty-card">
                  <div className="clubs-empty-icon">
                    <i className="feather-users" />
                  </div>

                  <h4>No clubs found</h4>
                  <p>Try another search or category filter.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Popupchat />
      <Appfooter />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

        .clubs-main-page {
          width: 100%;
          min-height: 100vh;
          padding-top: 58px;
          padding-left: 285px;
          padding-right: 315px;
          overflow-x: hidden;
          font-family: 'Inter', sans-serif;
          background: linear-gradient(180deg, #f8f9fc 0%, #f1f3f8 100%);
        }

        .clubs-middle-bottom,
        .clubs-middle-left {
          width: 100%;
          max-width: 100%;
        }

        .clubs-page-wrapper {
          width: 100%;
          max-width: 900px;
          margin-left: 22px;
          margin-top: 28px;
          padding-bottom: 40px;
        }

        .clubs-header-card {
          margin-bottom: 18px;
          padding: 14px 16px;
          border: 1px solid #e2eaf6;
          border-radius: 16px;
          background:
            linear-gradient(
              135deg,
              #ffffff 0%,
              #f7faff 58%,
              #f1f6ff 100%
            );
          box-shadow: 0 5px 16px rgba(10, 35, 84, 0.045);
        }

        .clubs-header-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 10px;
        }

        .clubs-page-badge {
          display: none;
        }

        .clubs-title {
          margin: 0 0 4px;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 24px;
          font-weight: 700;
          letter-spacing: -0.01em;
        }

        .clubs-subtitle {
          margin: 0;
          color: #8a93a3;
          font-size: 13px;
          line-height: 1.5;
        }

        .clubs-count-box {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          min-height: 26px;
          padding: 0 9px;
          flex-shrink: 0;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.78);
          white-space: nowrap;
        }

        .clubs-count-box strong,
        .clubs-count-box span {
          color: #5f7fae;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
        }

        .clubs-count-box strong,
        .clubs-count-box span {
          display: inline;
          margin: 0;
        }

        .clubs-filter-row {
          display: grid;
          grid-template-columns: minmax(0, 420px) 230px;
          gap: 10px;
          align-items: center;
        }

        .clubs-search-box {
          position: relative;
          width: 100%;
          height: 40px;
          display: flex;
          align-items: center;
        }

        .clubs-search-box i {
          position: absolute;
          left: 14px;
          top: 50%;
          z-index: 1;
          color: #9aa4b2;
          font-size: 14px;
          transform: translateY(-50%);
          pointer-events: none;
        }

        .clubs-search-box input {
          width: 100%;
          height: 40px;
          border: 1px solid #e5e8ef;
          border-radius: 10px;
          padding: 0 12px 0 36px;
          outline: none;
          color: #1f2937;
          background: rgba(255,255,255,0.92);
          font-size: 13px;
          box-shadow: inset 0 1px 2px rgba(15,23,42,.05);
          transition: box-shadow .2s ease, border-color .2s ease;
        }

        .clubs-search-box input::placeholder {
          color: #9aa4b2;
        }

        .clubs-search-box input:focus {
          border-color: #2F6FE4;
          box-shadow:
            inset 0 1px 2px rgba(15,23,42,.04),
            0 0 0 3px rgba(47,111,228,.10);
        }

        .clubs-select-wrap {
          position: relative;
          height: 40px;
        }

        .clubs-select-wrap select {
          width: 100%;
          height: 40px;
          border: 1px solid #e5e8ef;
          border-radius: 10px;
          padding: 0 34px 0 12px;
          appearance: none;
          outline: none;
          color: #475569;
          background: rgba(255,255,255,0.92);
          font-size: 12px;
          font-weight: 600;
        }

        .clubs-select-wrap select:focus {
          border-color: #2F6FE4;
          box-shadow: 0 0 0 3px rgba(47,111,228,.10);
        }

        .clubs-select-wrap i {
          position: absolute;
          right: 12px;
          top: 50%;
          color: #94a3b8;
          font-size: 14px;
          transform: translateY(-50%);
          pointer-events: none;
        }

        .clubs-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
        }

        .club-card {
          min-width: 0;
          overflow: hidden;
          padding: 14px;
          border: 1px solid #e9ecf2;
          border-radius: 16px;
          background: #fff;
          box-shadow: 0 2px 8px rgba(15,23,42,.05);
          transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease;
        }

        .club-card:hover {
          transform: translateY(-2px);
          border-color: #c9d9f8;
          box-shadow: 0 12px 28px rgba(10,35,84,.10);
        }

        .club-top {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .club-avatar {
          position: relative;
          width: 46px;
          height: 46px;
          min-width: 46px;
          overflow: hidden;
          border: 1px solid #e4eaf4;
          border-radius: 13px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #eef4ff;
        }

        .club-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .club-avatar-fallback {
          width: 100%;
          height: 100%;
          display: none;
          align-items: center;
          justify-content: center;
          color: #5f7fae;
          font-size: 20px;
        }

        .club-avatar-fallback.show { display: flex; }

        .club-main-info {
          flex: 1;
          min-width: 0;
        }

        .club-main-info h4 {
          margin: 0;
          overflow: hidden;
          color: #0f1b34;
          font-family: 'Fraunces', serif;
          font-size: 14px;
          font-weight: 600;
          line-height: 1.35;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .club-main-info span {
          display: block;
          margin-top: 4px;
          overflow: hidden;
          color: #2F6FE4;
          font-size: 10.5px;
          font-weight: 600;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .club-stats-row {
          display: grid;
          grid-template-columns: repeat(2, minmax(0,1fr));
          gap: 8px;
          margin-top: 12px;
        }

        .club-stat-box {
          padding: 8px;
          border: 1px solid #edf1f6;
          border-radius: 10px;
          text-align: center;
          background: #f8fafc;
        }

        .club-stat-box h5 {
          margin: 0 0 2px;
          color: #2F6FE4;
          font-size: 15px;
          font-weight: 700;
        }

        .club-stat-box span {
          color: #8a93a3;
          font-size: 10.5px;
          font-weight: 500;
        }

        .club-action-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          margin-top: 12px;
        }

        .club-btn {
          min-height: 34px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          cursor: pointer;
          transition: .2s ease;
        }

        .club-btn-follow {
          border: 1px solid #2F6FE4;
          background: #2F6FE4;
          color: #fff;
        }

        .club-btn-follow:hover { background: #1f57c7; }

        .club-btn-following {
          border: 1px solid #d7eadf;
          background: #f2fbf6;
          color: #198754;
          cursor: default;
        }

        .club-btn-view {
          border: 1px solid #dce7f7;
          background: #f5f8ff;
          color: #2F6FE4;
        }

        .club-btn-view:hover { background: #edf3ff; }

        .clubs-loader-card,
        .clubs-empty-card {
          min-height: 200px;
          padding: 28px 22px;
          border: 1px dashed #dbe2ec;
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          background: #fff;
        }

        .clubs-loader-card p {
          margin: 10px 0 0;
          color: #94a3b8;
          font-size: 11.5px;
        }

        .clubs-empty-icon {
          width: 44px;
          height: 44px;
          margin-bottom: 11px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #5f7fae;
          background: #eef4fb;
          font-size: 20px;
        }

        .clubs-empty-card h4 {
          margin: 0;
          color: #475569;
          font-size: 14px;
          font-weight: 700;
        }

        .clubs-empty-card p {
          margin: 6px 0 0;
          color: #94a3b8;
          font-size: 11.5px;
        }

        @media (max-width: 1400px) {
          .clubs-main-page { padding-right: 290px; }
        }

        @media (max-width: 1200px) {
          .clubs-main-page {
            padding-left: 20px;
            padding-right: 20px;
          }

          .clubs-page-wrapper {
            margin-left: 0;
            max-width: 100%;
          }

          .clubs-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 820px) {
          .clubs-grid { grid-template-columns: 1fr; }
        }

        @media (max-width: 768px) {
          .clubs-main-page {
            padding-top: 16px;
            padding-left: 12px;
            padding-right: 12px;
          }

          .clubs-middle-bottom,
          .clubs-middle-left {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .clubs-page-wrapper {
            margin-top: 0;
            margin-left: 0;
            max-width: 100%;
            padding-bottom: 76px;
          }

          .clubs-header-card {
            padding: 12px 13px;
            border-radius: 14px;
          }

          .clubs-title { font-size: 21px; }

          .clubs-filter-row { grid-template-columns: 1fr; }
        }

        @media (max-width: 480px) {
          .clubs-header-top { flex-direction: column; }
        }
      `}</style>
    </Fragment>
  );
};

export default ClubsPage;